import { DateTime } from 'luxon';
import React from 'react';
import { DateInput, FormCell, NumberInput, Select } from 'ui-toolkit';
import { calculatePeriodDays, updateBuyerReimbursementComputedFields } from '../utilities/commonComputations';
import generatePaymentPeriodOptions from '../utilities/generatePaymentPeriodOptions';
import partsFromDate, { generateDateText } from '../utilities/partsFromDate';
import { createOnChangeHandlerGenerator } from '../utilities/createOnChangeHandlerGenerator';
import { transformLabelValueToValue } from '../utilities/transformLabelValueToValue';
import { transformDateInputToValue } from '../utilities/transformDateInputToValue';
import IHasItemDataProps from './IHasItemDataProps';
import IHasItemEventProps from './IHasItemEventProps';
import IHasMetaDataModalProps from './IHasMetaDataModalProps';
import IHasMetaDataModalState from './IHasMetaDataModalState';
import { IItemWithoutShowSellerProps } from './IItemProps';
import PurchaseItemMetaDataModal, { IPurchaseItemMetaDataModalRef } from './PurchaseItemMetaDataModal';
import SharedPurchaseItemCells from './SharedPurchaseItemCells';
import './_BuyerReimbursement.scss';
import './_ModalPurchasePresentationItem.scss';
import { IBuyerReimbursementFromSellerViewItem } from 'ui-toolkit/cdItems/interfaces/ICDViewItem';
import { eReimbursements } from 'ui-toolkit/enums/eReimbursments';
import { convertToFloat } from '../../../ui-toolkit/utilities/convertToFloat';

const hasValue = (value?: any | null) => value !== undefined && value !== null;

const labelHash = {
  [eReimbursements.city_and_town_taxes]: 'City/Town Taxes',
  [eReimbursements.county_taxes]: 'CountyTaxes',
  [eReimbursements.assessments]: 'Assessments',
};

export interface IBuyerReimbursementFromSellerProps
  extends IItemWithoutShowSellerProps,
    IHasItemDataProps<IBuyerReimbursementFromSellerViewItem>,
    IHasItemEventProps<IBuyerReimbursementFromSellerViewItem>,
    IHasMetaDataModalProps {
  onFocus?: () => void;
  onBlur?: (event: React.FocusEvent) => void;
}

const paymentPeriodAsOptions = generatePaymentPeriodOptions();

type IBuyerReimbursementFromSellerState = IHasMetaDataModalState;

export default class BuyerReimbursementFromSeller extends React.Component<
  IBuyerReimbursementFromSellerProps,
  IBuyerReimbursementFromSellerState
> {
  public state = { expanded: !this.props.disableAutoExpand && this.props.data.new };
  private modal = React.createRef<IPurchaseItemMetaDataModalRef>();

  // private firstInput: HTMLInputElement | null = null;// TODO: add support for focus to Select/Creatable and enable this "autofocus" feature

  public render() {
    const { data, onChange, hasEditPermissions } = this.props;
    const fromDate = partsFromDate(data.date_from);
    const toDate = partsFromDate(data.date_to);
    const period = data.billing_period;
    const selectedPaymentPeriodOption = paymentPeriodAsOptions.find((option) => option.value === period);

    const generateDefaultOnChangeEvent = createOnChangeHandlerGenerator(data, onChange);
    const generateComputedOnChangeEvent = createOnChangeHandlerGenerator(
      data,
      (x: IBuyerReimbursementFromSellerViewItem) =>
        onChange(updateBuyerReimbursementComputedFields(x, 'seller_at_closing'))
    );

    const sellerAtClosingOnChangeEvent = generateDefaultOnChangeEvent('seller_at_closing');
    const periodAmountOnChangeEvent = generateComputedOnChangeEvent('period_amount');
    const paymentPeriodOnChangeEvent = generateComputedOnChangeEvent('billing_period', transformLabelValueToValue);
    const fromOnChangeEvent = generateComputedOnChangeEvent('date_from', transformDateInputToValue);
    const toOnChangeEvent = generateComputedOnChangeEvent('date_to', transformDateInputToValue);

    return (
      <SharedPurchaseItemCells
        data={data}
        sellerAtClosingDisplay="text"
        className="modal-purchase-presentation-item buyer-reimbursement"
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        onChange={onChange}
        showRemove={false}
        hasEditPermissions={hasEditPermissions}
      >
        <PurchaseItemMetaDataModal
          expanded={this.state.expanded}
          label={`${labelHash[data.sub_type]} ${generateDateText(fromDate)} to ${generateDateText(toDate)}`}
          onToggle={this.onModalToggleEvent}
          ref={this.modal}
        >
          <div className="buyer-reimbursement__fields">
            <div>
              <FormCell label={'Billing Period'} hintText={this.hintTextPeriod(data)}>
                <Select
                  value={selectedPaymentPeriodOption}
                  onChange={paymentPeriodOnChangeEvent}
                  options={paymentPeriodAsOptions}
                  isClearable={true}
                  isDisabled={!hasEditPermissions}
                />
              </FormCell>
            </div>

            <div>
              <NumberInput
                mode="currency"
                label="Billing Amount"
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                onChange={periodAmountOnChangeEvent}
                value={data.period_amount}
                readOnly={!hasEditPermissions}
                debounceOnValueChange
              />
            </div>

            <FormCell hintText={this.hintTextDays(data)}>
              <DateInput
                label="From Date"
                name="date_from"
                value={fromDate ? `${fromDate.year}-${fromDate.month}-${fromDate.date}` : null}
                onBlur={this.onBlur}
                onChange={fromOnChangeEvent}
                readOnly={!hasEditPermissions}
                attachPortalInsideContainter={true}
              />
            </FormCell>
            <DateInput
              label="To Date"
              name="date_to"
              value={toDate ? `${toDate.year}-${toDate.month}-${toDate.date}` : null}
              onBlur={this.onBlur}
              onChange={toOnChangeEvent}
              readOnly={!hasEditPermissions}
              attachPortalInsideContainter={true}
            />

            <FormCell
              label="Amount"
              className="buyer-reimbursement__fields__full-line"
              hintText={this.hintTextAmount(data)}
            >
              <NumberInput
                disabled={!!selectedPaymentPeriodOption}
                mode={'currency'}
                onChange={sellerAtClosingOnChangeEvent}
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                readOnly={!!selectedPaymentPeriodOption || !hasEditPermissions}
                value={data.seller_at_closing}
                debounceOnValueChange
              />
            </FormCell>
          </div>
        </PurchaseItemMetaDataModal>
      </SharedPurchaseItemCells>
    );
  }

  private hintTextAmount = (data: IBuyerReimbursementFromSellerViewItem): string | null => {
    const hasComputedDataFields =
      hasValue(data.period_amount) &&
      hasValue(data.date_from) &&
      hasValue(data.date_to) &&
      paymentPeriodAsOptions.find((option) => option.value === data.billing_period);
    if (hasComputedDataFields) {
      const { date_to, date_from, period_amount } = data;
      const periodDays = calculatePeriodDays(data);
      const amountPerPeriod = convertToFloat(period_amount);

      const dateFrom = DateTime.fromJSDate(date_from!);
      const dateTo = DateTime.fromJSDate(date_to!);

      const diffDays = dateTo.diff(dateFrom, ['days']).days + 1;

      const amount = amountPerPeriod * (diffDays / periodDays);

      return `${diffDays}/${periodDays} * $${amountPerPeriod} = $${amount.toFixed(2)}`;
    }
    return '';
  };

  private hintTextDays = (data: IBuyerReimbursementFromSellerViewItem): string | null => {
    const hasComputedDataFields =
      hasValue(data.period_amount) &&
      hasValue(data.date_from) &&
      hasValue(data.date_to) &&
      paymentPeriodAsOptions.find((option) => option.value === data.billing_period);
    if (hasComputedDataFields) {
      const { date_to, date_from } = data;

      const dateFrom = DateTime.fromJSDate(date_from!);
      const dateTo = DateTime.fromJSDate(date_to!);

      const diffDays = dateTo.diff(dateFrom, ['days']).days + 1;

      return `Number of days : ${diffDays}`;
    }
    return '';
  };

  private hintTextPeriod = (data: IBuyerReimbursementFromSellerViewItem): string | null => {
    const hasComputedDataFields =
      hasValue(data.period_amount) &&
      hasValue(data.date_from) &&
      hasValue(data.date_to) &&
      paymentPeriodAsOptions.find((option) => option.value === data.billing_period);
    if (hasComputedDataFields) {
      const periodDays = calculatePeriodDays(data);
      return `Number of days in billing period : ${periodDays}`;
    }
    return '';
  };

  private onBlur = (event: React.FocusEvent) => {
    const { onBlur } = this.props;
    const node = this.modal.current;
    if (node && event.relatedTarget && !node.modalContains(event.relatedTarget as HTMLElement)) {
      this.setState({
        expanded: false,
      });
    }
    if (onBlur) {
      onBlur(event);
    }
  };

  private onFocus = () => {
    const { onFocus } = this.props;
    if (onFocus) {
      onFocus();
    }
  };

  private onModalToggleEvent = (expanded: boolean) => this.setState({ expanded });
}
